import { nextPow2, isPow2 } from '../../../../maths/misc/pow2.mjs';
import { deprecation, v8_21_0 } from '../../../../utils/logging/deprecation.mjs';
import { warn } from '../../../../utils/logging/warn.mjs';
import { GlobalResourceRegistry } from '../../../../utils/pool/GlobalResourceRegistry.mjs';
import { TextureSource } from './sources/TextureSource.mjs';
import { Texture } from './Texture.mjs';
import { TextureStyle } from './TextureStyle.mjs';
import { ScreenSizeRegistry } from './utils/ScreenSizeRegistry.mjs';

"use strict";
let count = 0;
const maxKeyDimension = 32767;
const scaleBit = 4294967296;
const formatBit = 8589934592;
const formatIds = /* @__PURE__ */ Object.create(null);
let nextFormatId = 0;
function formatId(format) {
  const id = formatIds[format];
  if (id !== void 0) return id;
  return formatIds[format] = nextFormatId++;
}
function bucketKey(width, height, antialias, autoGenerateMipmaps, format, scaleMode) {
  const packed = (width << 17) + (height << 2) + ((autoGenerateMipmaps ? 1 : 0) << 1) + (antialias ? 1 : 0);
  const scale = scaleMode === "nearest" ? 1 : 0;
  return packed + scale * scaleBit + formatId(format) * formatBit;
}
class TexturePoolClass {
  /**
   * @param textureOptions - options that will be passed to BaseRenderTexture constructor
   * @param {SCALE_MODE} [textureOptions.scaleMode] - See {@link SCALE_MODE} for possible values.
   */
  constructor(textureOptions) {
    /** idle textures, keyed by format, scale mode, size and flags */
    this._buckets = /* @__PURE__ */ new Map();
    /** the bucket key of every texture handed out, by texture uid */
    this._poolKey = /* @__PURE__ */ Object.create(null);
    /** the style the pool created for every texture handed out, by texture uid */
    this._poolStyle = /* @__PURE__ */ Object.create(null);
    /** the screens this pool is sizing its textures for */
    this._screens = new ScreenSizeRegistry();
    this._enableFullScreen = false;
    this.textureOptions = textureOptions || {};
  }
  /**
   * A style built from the pool options. The pool no longer applies it to anything.
   * @deprecated since 8.21.0, pooled textures carry their own style; pass `scaleMode` in the request instead.
   */
  get textureStyle() {
    deprecation(v8_21_0, "TexturePool.textureStyle is no longer used, pooled textures carry their own style. Pass scaleMode in the request instead.");
    if (!this._textureStyle) this._textureStyle = new TextureStyle(this.textureOptions);
    return this._textureStyle;
  }
  set textureStyle(value) {
    deprecation(v8_21_0, "TexturePool.textureStyle is no longer used, pooled textures carry their own style. Pass scaleMode in the request instead.");
    this._textureStyle = value;
  }
  /**
   * Has no effect. The pool sizes textures to the screens registered with
   * {@link TexturePoolClass#setScreenSize|setScreenSize}.
   * @deprecated since 8.21.0
   */
  get enableFullScreen() {
    deprecation(v8_21_0, "TexturePool.enableFullScreen is no longer used, the pool sizes textures to the screens registered with setScreenSize.");
    return this._enableFullScreen;
  }
  set enableFullScreen(value) {
    deprecation(v8_21_0, "TexturePool.enableFullScreen is no longer used, the pool sizes textures to the screens registered with setScreenSize.");
    this._enableFullScreen = value;
  }
  createTexture(...args) {
    let options = args[0];
    if (typeof options === "number") {
      deprecation(v8_21_0, "TexturePool.createTexture params are now an options object. See params: { width, height, antialias, autoGenerateMipmaps }");
      options = {
        width: options,
        height: args[1],
        antialias: args[2] ?? false,
        autoGenerateMipmaps: args[3] ?? false
      };
    }
    const format = options.format ?? this.textureOptions.format ?? TextureSource.defaultOptions.format;
    const scaleMode = options.scaleMode ?? this.textureOptions.scaleMode ?? TextureStyle.defaultOptions.scaleMode;
    const textureSource = new TextureSource({
      ...this.textureOptions,
      width: options.width,
      height: options.height,
      resolution: 1,
      antialias: options.antialias ?? false,
      autoGarbageCollect: false,
      autoGenerateMipmaps: options.autoGenerateMipmaps ?? false,
      format,
      scaleMode
    });
    return new Texture({
      source: textureSource,
      label: `texturePool_${count++}`
    });
  }
  getOptimalTexture(...args) {
    let options = args[0];
    if (typeof options === "number") {
      deprecation(v8_21_0, "TexturePool.getOptimalTexture params are now an options object. See params: { width, height, resolution, antialias, autoGenerateMipmaps }");
      options = {
        width: options,
        height: args[1],
        resolution: args[2] ?? 1,
        antialias: args[3] ?? false,
        autoGenerateMipmaps: args[4] ?? false
      };
    }
    const frameWidth = options.width;
    const frameHeight = options.height;
    const resolution = options.resolution ?? 1;
    const antialias = options.antialias ?? false;
    const autoGenerateMipmaps = options.autoGenerateMipmaps ?? false;
    const format = options.format ?? this.textureOptions.format ?? TextureSource.defaultOptions.format;
    const scaleMode = options.scaleMode ?? this.textureOptions.scaleMode ?? TextureStyle.defaultOptions.scaleMode;
    const { width: textureWidth, height: textureHeight } = this.getOptimalSize(
      frameWidth,
      frameHeight,
      resolution
    );
    if (textureWidth > maxKeyDimension || textureHeight > maxKeyDimension) {
      warn(`TexturePool: ${textureWidth}x${textureHeight} is larger than the ${maxKeyDimension}px pool key limit, textures of this size may be pooled together`);
    }
    const key = bucketKey(textureWidth, textureHeight, antialias, autoGenerateMipmaps, format, scaleMode);
    let bucket = this._buckets.get(key);
    if (!bucket) {
      bucket = [];
      this._buckets.set(key, bucket);
    }
    let texture = bucket.pop();
    if (!texture) {
      texture = this.createTexture({
        width: textureWidth,
        height: textureHeight,
        antialias,
        autoGenerateMipmaps,
        format,
        scaleMode
      });
      this._poolStyle[texture.uid] = texture.source.style;
    }
    texture.source._resolution = resolution;
    texture.source.width = textureWidth / resolution;
    texture.source.height = textureHeight / resolution;
    texture.source.pixelWidth = textureWidth;
    texture.source.pixelHeight = textureHeight;
    texture.frame.x = 0;
    texture.frame.y = 0;
    texture.frame.width = frameWidth;
    texture.frame.height = frameHeight;
    texture.updateUvs();
    this._poolKey[texture.uid] = key;
    return texture;
  }
  /**
   * The backing size, in physical pixels, that {@link TexturePoolClass#getOptimalTexture|getOptimalTexture}
   * would allocate for a request, without taking a texture from the pool.
   *
   * Each axis is the next power of two, or the smallest registered screen the request fits inside
   * (see {@link TexturePoolClass#setScreenSize|setScreenSize}). Use it when a consumer needs to know
   * where a request's content will sit inside its pooled texture before it has one, such as a uniform
   * that maps content space onto texture space.
   * @param frameWidth - The minimum width of the texture.
   * @param frameHeight - The minimum height of the texture.
   * @param resolution - The resolution of the texture.
   * @returns The width and height the pooled texture would have, in physical pixels.
   */
  getOptimalSize(frameWidth, frameHeight, resolution = 1) {
    const pixelWidth = Math.ceil(frameWidth * resolution - 1e-6);
    const pixelHeight = Math.ceil(frameHeight * resolution - 1e-6);
    const po2Width = nextPow2(pixelWidth);
    const screenWidth = this._screens.getFittingWidth(pixelWidth);
    const po2Height = nextPow2(pixelHeight);
    const screenHeight = this._screens.getFittingHeight(pixelHeight);
    return {
      width: screenWidth !== void 0 ? Math.min(screenWidth, po2Width) : po2Width,
      height: screenHeight !== void 0 ? Math.min(screenHeight, po2Height) : po2Height
    };
  }
  /**
   * Gets a pooled texture matching the dimensions and resolution of the given texture.
   *
   * This is a convenience wrapper around {@link TexturePoolClass#getOptimalTexture|getOptimalTexture}
   * that copies width, height, and resolution from an existing texture. Useful when a filter needs
   * a temporary texture the same size as its input (e.g., for multi-pass blur).
   * @param texture - The texture whose dimensions to match.
   * @param antialias - Whether to use antialias on the pooled texture. Defaults to `false`.
   * @returns A pooled texture with power-of-two or screen sized backing dimensions at the source resolution.
   */
  getSameSizeTexture(texture, antialias = false) {
    const source = texture.source;
    return this.getOptimalTexture({
      width: texture.width,
      height: texture.height,
      resolution: source._resolution,
      antialias
    });
  }
  /**
   * Returns a texture to the pool so it can be reused by future
   * {@link TexturePoolClass#getOptimalTexture|getOptimalTexture}
   * or {@link TexturePoolClass#getSameSizeTexture|getSameSizeTexture} calls.
   *
   * If you gave the texture a style of your own after obtaining it (a different address mode, anisotropy
   * or similar), pass `resetStyle = true` so the pool puts its own style back. Otherwise your style stays
   * on the texture and the next consumer inherits it.
   * @param renderTexture - The texture to return to the pool.
   * @param resetStyle - When `true`, restores the style the pool created for this texture. Defaults to `false`.
   */
  returnTexture(renderTexture, resetStyle = false) {
    const uid = renderTexture.uid;
    const key = this._poolKey[uid];
    if (key === void 0) {
      warn("TexturePool: returnTexture was passed a texture that did not come from this pool, ignoring it");
      return;
    }
    const poolStyle = this._poolStyle[uid];
    if (resetStyle && renderTexture.source.style !== poolStyle) {
      renderTexture.source.style = poolStyle;
    }
    const textures = this._buckets.get(key);
    if (!textures) {
      delete this._poolKey[uid];
      delete this._poolStyle[uid];
      renderTexture.destroy(true);
      return;
    }
    textures.push(renderTexture);
  }
  /**
   * Registers the screen size of a renderer with the pool, in physical pixels.
   *
   * While a screen is registered, a request that fits inside it on an axis is given that screen's size on
   * that axis instead of the next power of two, which stops a full screen filter from allocating a texture
   * far larger than the screen. Requests larger than every registered screen on an axis keep the power of
   * two size - the pool never rounds a request up to a screen it does not fit in.
   * @param rendererUid - The uid of the renderer, used to update or remove this screen later.
   * @param pixelWidth - The width of the screen in physical pixels.
   * @param pixelHeight - The height of the screen in physical pixels.
   */
  setScreenSize(rendererUid, pixelWidth, pixelHeight) {
    if (!this._screens.set(rendererUid, pixelWidth, pixelHeight)) return;
    this._pruneScreenTextures();
  }
  /**
   * Removes a screen previously registered with
   * {@link TexturePoolClass#setScreenSize|setScreenSize}, destroying any idle textures that were
   * only being kept for it.
   * @param rendererUid - The uid the screen was registered with.
   */
  removeScreen(rendererUid) {
    if (!this._screens.remove(rendererUid)) return;
    this._pruneScreenTextures();
  }
  /**
   * Destroys the idle textures in every bucket that has a non power of two dimension matching no live
   * screen. Power of two buckets are always kept, as any request can fall back to them.
   */
  _pruneScreenTextures() {
    for (const [key, textures] of this._buckets) {
      const packed = key >>> 0;
      const width = packed >>> 17;
      const height = packed >>> 2 & 32767;
      if ((isPow2(width) || this._screens.hasWidth(width)) && (isPow2(height) || this._screens.hasHeight(height))) continue;
      this._dropTextures(textures, true);
      this._buckets.delete(key);
    }
  }
  /**
   * Forgets idle textures were ever handed out, so a later return is ignored, and optionally destroys them.
   * @param textures - The textures of one bucket.
   * @param destroy - Whether to destroy the textures as well.
   */
  _dropTextures(textures, destroy) {
    for (let i = 0; i < textures.length; i++) {
      const texture = textures[i];
      delete this._poolKey[texture.uid];
      delete this._poolStyle[texture.uid];
      if (destroy) texture.destroy(true);
    }
  }
  /**
   * Clears the pool.
   * @param destroyTextures - Destroy all stored textures.
   */
  clear(destroyTextures) {
    for (const textures of this._buckets.values()) {
      this._dropTextures(textures, destroyTextures !== false);
    }
    this._buckets.clear();
  }
}
const TexturePool = new TexturePoolClass();
GlobalResourceRegistry.register(TexturePool);

export { TexturePool, TexturePoolClass };
//# sourceMappingURL=TexturePool.mjs.map
